import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { HomePage } from './pages/HomePage';
import { PortfolioPage } from './pages/PortfolioPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { SegmentDetailPage } from './pages/SegmentDetailPage';
import { ResearchPage } from './pages/ResearchPage';
import { LetterheadPage } from './pages/LetterheadPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { useDocumentMeta } from './hooks/useDocumentMeta';
import { CookieConsentProvider, useCookieConsentContext } from './lib/CookieConsentContext';
import { CookieConsent } from './components/ui/CookieConsent';

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        // Wait a brief moment for pages to load/render
        const timer = setTimeout(() => {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 150);
        return () => clearTimeout(timer);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
};

/**
 * English pages live under /en/... (Italian stays unprefixed) so each language
 * is a distinct, indexable URL rather than the same URL changing content based
 * on the visitor's browser — see i18n/config.ts. Routes below are defined once,
 * unprefixed; React Router's `basename` transparently applies the /en prefix to
 * every route match and every <Link>, so no internal link needs to know about it.
 * Fixed once per full page load — the language switcher does a real navigation
 * (not client-side routing) when crossing between /en and unprefixed.
 */
const basename =
  typeof window !== 'undefined' &&
  (window.location.pathname === '/en' || window.location.pathname.startsWith('/en/'))
    ? '/en'
    : '';

const AppContent = () => {
  useDocumentMeta();
  const { status, accept, reject } = useCookieConsentContext();

  return (
    <BrowserRouter basename={basename}>
      <ScrollToTop />
      <Layout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/portfolio/:id" element={<ProjectDetailPage />} />
          <Route path="/segments/:id" element={<SegmentDetailPage />} />
          <Route path="/research" element={<ResearchPage />} />
          <Route path="/intestazione" element={<LetterheadPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Layout>
      <CookieConsent status={status} onAccept={accept} onReject={reject} />
    </BrowserRouter>
  );
};

function App() {
  return (
    <CookieConsentProvider>
      <AppContent />
    </CookieConsentProvider>
  );
}

export default App;
